import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_REFRESH_MS = 2 * 60 * 60 * 1000; // 2 hours
const DEFAULT_HIDDEN_REFRESH_MS = 10 * 60 * 1000; // 10 minutes

function useIframeWatchdog(
  baseSrc,
  { refreshMs = DEFAULT_REFRESH_MS, hiddenRefreshMs = DEFAULT_HIDDEN_REFRESH_MS } = {}
) {
  const [refreshToken, setRefreshToken] = useState(() => Date.now());
  const hiddenAtRef = useRef(null);

  const refreshIframe = useCallback(() => {
    setRefreshToken(Date.now());
  }, []);

  useEffect(() => {
    if (!baseSrc) {
      return undefined;
    }

    const intervalId = window.setInterval(refreshIframe, refreshMs);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [baseSrc, refreshIframe, refreshMs]);

  useEffect(() => {
    if (!baseSrc) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }

      if (
        hiddenAtRef.current &&
        Date.now() - hiddenAtRef.current >= hiddenRefreshMs
      ) {
        refreshIframe();
      }

      hiddenAtRef.current = null;
    };

    window.addEventListener('online', refreshIframe);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', refreshIframe);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [baseSrc, hiddenRefreshMs, refreshIframe]);

  const iframeSrc = useMemo(() => {
    if (!baseSrc) {
      return '';
    }

    const joinChar = baseSrc.includes('?') ? '&' : '?';
    return `${baseSrc}${joinChar}watchdog=${refreshToken}`;
  }, [baseSrc, refreshToken]);

  return { iframeSrc, refreshIframe };
}

export default useIframeWatchdog;
